import {
  ControllerProps,
  FieldValues,
  Path,
  UseControllerReturn,
  UseControllerProps,
} from 'react-hook-form';
import { TextFieldProps, TextField } from '@mui/material';

import { mapBooleanValueMessage, mapNumberValueMessage } from './valueMessage';
import { makeControlledInput } from './makeControlledInput';

/**
 * This is the expanded version of controlledTextField,
 * which gives more control on the props
 *
 * @example
 * ```tsx
 * const CurrencyCodeInput = controlledInputNewNew<TextFieldProps, FieldValues>()({
 *   name: 'currencyCode',
 *   defaultControllerProps: {
 *     rules: { required: true },
 *   },
 *   Component: TextField, // You can use another component that has the same props
 *   propsMapper: mapControllerTextField, // You can wrap this function if you want to customize something
 * });
 * ```
 *
 * @param controllerRenderProps - props given to the `render` function by react-hook-form
 * @param controllerProps - default props for Controller given to the generated component
 * @param defaultInputProps - default props for the generated component, that will be forwarded to the TextField
 */
export const mapControllerTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
>(
  controllerRenderProps: UseControllerReturn<TFieldValues, TName>,
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render'>,
  defaultInputProps?: Partial<TextFieldProps>
): TextFieldProps => {
  const {
    field: { onChange, onBlur, name, value, ref: inputRef },
    fieldState: { error },
  } = controllerRenderProps;
  const rules = controllerProps?.rules;
  const controllerRequired = rules?.required;
  const controllerMin = rules?.min;
  const controllerMax = rules?.max;
  return {
    ...defaultInputProps,
    onChange,
    onBlur,
    name,
    value,
    inputRef,
    required: mapBooleanValueMessage(controllerRequired),
    inputProps: {
      min: mapNumberValueMessage(controllerMin),
      max: mapNumberValueMessage(controllerMax),
    },
    error: Boolean(error),
  };
};

/**
 * Shorthand to create Controlled TextForm components
 *
 * @example
 * ```tsx
 * const AmountInput = controlledTextField<FieldValues>()('amount', {
 *   rules: { required: true, min: 1 },
 * });
 * ```
 */
export const controlledTextField =
  <TFieldValues extends FieldValues = FieldValues>() =>
  <
    TDefaultControllerProps extends Partial<UseControllerProps<TFieldValues, TName>>,
    TName extends Path<TFieldValues>
  >(
    name: TName,
    defaultControllerProps?: TDefaultControllerProps
  ) =>
    makeControlledInput<TFieldValues>()({
      name,
      defaultControllerProps,
      Component: TextField,
      propsMapper: mapControllerTextField,
    });
